/**
 * Create a seeded pseudo-random number generator using mulberry32 algorithm
 * @param seed - Seed string to initialize the generator
 * @returns A function that returns a random number between 0 and 1
 */
export function createSeededRandom(seed: string): () => number {
    // Convert seed string to numeric hash
    let h = 0;
    for (let i = 0; i < seed.length; i++) {
        h = Math.imul(31, h) + seed.charCodeAt(i) | 0;
    }
    // Mulberry32 PRNG
    return () => {
        h |= 0;
        h = h + 0x6D2B79F5 | 0;
        let t = Math.imul(h ^ h >>> 15, 1 | h);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}

/**
 * Generate a random seed string
 */
export function generateSeed(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

/**
 * Fisher-Yates shuffle using Math.random()
 * Shuffles array in-place and returns it
 */
export function shuffle<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j]!, array[i]!];
    }
    return array;
}

/**
 * Fisher-Yates shuffle using a seeded random function
 * Shuffles array in-place and returns it
 */
export function shuffleSeeded<T>(array: T[], rng: () => number): T[] {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [array[i], array[j]] = [array[j]!, array[i]!];
    }
    return array;
}

/**
 * Returns a random element from an array
 */
export function randomChoice<T>(array: T[]): T | undefined {
    if (array.length === 0) return undefined;
    return array[Math.floor(Math.random() * array.length)];
}

/**
 * Returns a random element from an array using a seeded random function
 */
export function randomChoiceSeeded<T>(array: T[], rng: () => number): T | undefined {
    if (array.length === 0) return undefined;
    return array[Math.floor(rng() * array.length)];
}

/**
 * Returns a random integer between min (inclusive) and max (exclusive)
 */
export function randomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min)) + min;
}

/**
 * Returns a random integer between min (inclusive) and max (exclusive) using seeded random
 */
export function randomIntSeeded(min: number, max: number, rng: () => number): number {
    return Math.floor(rng() * (max - min)) + min;
}
